import type { QueryClient } from '@tanstack/react-query';

import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import { deriveScreeningQueued } from '@/lib/applicationScreening';
import type { Application } from '@/types';

/** Avoid browser `ERR_INSUFFICIENT_RESOURCES` when bulk queues are large. */
const HYDRATE_CONCURRENCY = 4;
const HYDRATE_MAX_PER_SYNC = 20;
/** BE POST /bulk-processing-status hard limit — chunk larger hydrates. */
export const BULK_STATUS_API_MAX_PER_REQUEST = 25;
/** On F5 resume, hydrate the whole batch in one pass when possible. */
export const RESUME_HYDRATE_MAX_PER_SYNC = 50;

export type BulkProcessingStatusItem = {
  applicationId: string;
  jobPositionId: string;
  state: Application['state'];
  updatedAt: string;
  aiScore?: number;
  screeningQueued?: boolean;
};

function shouldFallbackBulkStatus(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  // 404 — endpoint missing on older BE; 400 — validation (e.g. batch >25 before chunk fix).
  return err.status === 404 || err.status === 400;
}

export function applyBulkStatusPatches(
  apps: Application[],
  items: BulkProcessingStatusItem[],
): Application[] {
  if (items.length === 0) return apps;
  const byId = new Map(items.map((item) => [item.applicationId, item]));
  const patched = apps.map((app) => {
    const patch = byId.get(app.id);
    if (!patch) return app;
    return {
      ...app,
      positionId: patch.jobPositionId || app.positionId,
      state: patch.state,
      ...(patch.aiScore !== undefined ? { aiScore: patch.aiScore } : {}),
      updatedAt: patch.updatedAt,
      screeningQueued: patch.screeningQueued ?? deriveScreeningQueued(patch.state, undefined),
    };
  });
  const existingIds = new Set(patched.map((a) => a.id));
  const additions = items
    .filter((item) => !existingIds.has(item.applicationId))
    .map((patch) => ({
      id: patch.applicationId,
      positionId: patch.jobPositionId,
      state: patch.state,
      ...(patch.aiScore !== undefined ? { aiScore: patch.aiScore } : {}),
      updatedAt: patch.updatedAt,
      screeningQueued: patch.screeningQueued ?? deriveScreeningQueued(patch.state, undefined),
    })) as Application[];
  return additions.length > 0 ? [...patched, ...additions] : patched;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      while (nextIndex < items.length) {
        const i = nextIndex++;
        results[i] = await fn(items[i]!);
      }
    },
  );
  await Promise.all(workers);
  return results;
}

/** States where bulk/async screening is finished (or parked) — safe to drop row spinner. */
const PROCESSING_SETTLED_STATES = new Set<Application['state']>([
  'AWAITING_DECISION',
  'REJECTED',
  'HIRED',
  'WITHDRAWN',
  'OFFER_DECLINED',
  'READY_TO_PROCESS',
  'PENDING_UPGRADE',
  'PIPELINE_ERROR',
]);

/** Clear row spinner only once the server reached a post-pipeline (or parked) state. */
export function shouldClearProcessingIndicator(state: Application['state']): boolean {
  return PROCESSING_SETTLED_STATES.has(state);
}

/** bulk-process-now enqueues async runs — list API often still returns READY/APPLIED_NEW. */
export function isBulkEnqueueListLagState(state: Application['state']): boolean {
  return state === 'READY_TO_PROCESS' || state === 'APPLIED_NEW';
}

/** Bulk targets still in-flight — list cache must be hydrated from detail (not "settled"). */
export function isBulkTargetPendingHydration(
  app: Pick<Application, 'state'> | undefined,
): boolean {
  if (!app) return true;
  return isBulkEnqueueListLagState(app.state) || app.state === 'PIPELINE_RUNNING';
}

/** Reconcile processingIds — keep bulk targets during list lag and while pipeline runs. */
export function shouldReconcileClearProcessingId(
  app: Pick<Application, 'id' | 'state'>,
  bulkTargetIds?: ReadonlySet<string>,
): boolean {
  if (bulkTargetIds?.has(app.id) && isBulkEnqueueListLagState(app.state)) {
    return false;
  }
  // Server PIPELINE_RUNNING — keep UI queue until a settled state (avoid instant "stuck" badge).
  if (app.state === 'PIPELINE_RUNNING') {
    return false;
  }
  return shouldClearProcessingIndicator(app.state);
}

export type ExpandBulkProcessingOptions = {
  /**
   * When false, skip target ids missing from apps (empty list cache on reload).
   * Defaults to true only when apps has rows.
   */
  includeUnknownTargets?: boolean;
};

/**
 * After reload, processingIds in session may be empty while targetIds remain.
 * Re-add unsettled bulk targets so filters/badges match the banner scope.
 */
export function expandProcessingIdsForBulkTargets(
  targetIds: readonly string[],
  processingIds: ReadonlySet<string>,
  apps: readonly Application[],
  options?: ExpandBulkProcessingOptions,
): Set<string> {
  const includeUnknown = options?.includeUnknownTargets ?? apps.length > 0;
  const out = new Set(processingIds);
  for (const id of targetIds) {
    if (out.has(id)) continue;
    const app = apps.find((a) => a.id === id);
    if (!app) {
      if (includeUnknown) out.add(id);
      continue;
    }
    if (shouldClearProcessingIndicator(app.state)) continue;
    out.add(id);
  }
  return out;
}

/** Clear "Stopping…" once abort finished or the row left the in-flight pipeline. */
export function shouldClearStoppingIndicator(state: Application['state']): boolean {
  return (
    state === 'AWAITING_DECISION'
    || state === 'REJECTED'
    || state === 'HIRED'
    || state === 'WITHDRAWN'
    || state === 'OFFER_DECLINED'
    || state === 'PIPELINE_ERROR'
    || state === 'PENDING_UPGRADE'
    || state === 'APPLIED_NEW'
    || state === 'READY_TO_PROCESS'
  );
}

/**
 * Banner "N of K" — rows with a settled server state (post-screening or parked).
 * Uses Firestore/list truth, not processingIds: reconcile can lag while AWAITING_DECISION
 * is already visible and would otherwise freeze the counter (e.g. 55/57 with 2 in queue done).
 */
export function countBulkBannerCompleted(
  targetIds: readonly string[],
  apps: readonly Application[],
  _processingIds: ReadonlySet<string>,
  _stoppingIds: ReadonlySet<string>,
  bulkTargetIds?: ReadonlySet<string>,
): number {
  return targetIds.filter((id) => {
    const app = apps.find((a) => a.id === id);
    if (!app) return false;
    if (bulkTargetIds?.has(id)) {
      if (isBulkEnqueueListLagState(app.state) || app.state === 'PIPELINE_RUNNING') {
        return false;
      }
    }
    return shouldClearProcessingIndicator(app.state) && !isBulkEnqueueListLagState(app.state);
  }).length;
}

/** @deprecated Use countBulkBannerCompleted — READY_TO_PROCESS counted as settled and inflated progress. */
export function countBulkTargetsSettled(
  targetIds: readonly string[],
  apps: readonly Application[],
): number {
  return countBulkBannerCompleted(targetIds, apps, new Set(), new Set(), new Set(targetIds));
}

/** Drop queue ids once Firestore state shows the row is no longer in-flight. */
export function reconcileQueueIds(
  applications: Application[],
  stoppingIds: Set<string>,
  processingIds: Set<string>,
  options?: { bulkTargetIds?: ReadonlySet<string> },
): { stoppingIds: Set<string>; processingIds: Set<string> } {
  const bulkTargetIds = options?.bulkTargetIds;
  const nextStopping = new Set(stoppingIds);
  const nextProcessing = new Set(processingIds);
  for (const app of applications) {
    if (nextStopping.has(app.id) && shouldClearStoppingIndicator(app.state)) {
      nextStopping.delete(app.id);
    }
    if (nextProcessing.has(app.id) && shouldReconcileClearProcessingId(app, bulkTargetIds)) {
      nextProcessing.delete(app.id);
    }
  }
  return { stoppingIds: nextStopping, processingIds: nextProcessing };
}

/**
 * bulk-process-now only enqueues PipelineRuns; a refetch right after the HTTP
 * response often still returns APPLIED_NEW. Keep in-flight rows out of "New"
 * while processingIds is active.
 */
/** Active bulk-run scope (banner targetIds + in-flight queue ids). */
export function activeBulkBatchIdSet(
  targetIds: readonly string[],
  processingIds: Set<string>,
  stoppingIds: Set<string>,
): Set<string> {
  const scope = new Set(targetIds);
  for (const id of processingIds) scope.add(id);
  for (const id of stoppingIds) scope.add(id);
  return scope;
}

/** Bump `updatedAt` so Updated ↓ sort surfaces in-flight bulk rows immediately. */
export function touchApplicationUpdatedNow(
  app: Application,
  patch: Partial<Application> = {},
): Application {
  return { ...app, ...patch, updatedAt: new Date().toISOString() };
}

export function coalesceInflightApplicationStates(
  apps: Application[],
  processingIds: Set<string>,
): Application[] {
  if (processingIds.size === 0) return apps;
  return apps.map(app => {
    if (!processingIds.has(app.id)) return app;
    // List API often still shows READY/APPLIED while the bulk run is active.
    if (isBulkEnqueueListLagState(app.state)) {
      return touchApplicationUpdatedNow(app, { state: 'PIPELINE_RUNNING' });
    }
    // Server already settled — never keep optimistic PIPELINE_RUNNING overlay.
    if (shouldClearProcessingIndicator(app.state)) return app;
    if (app.state === 'APPLIED_NEW' || app.state === 'PIPELINE_RUNNING') {
      return touchApplicationUpdatedNow(app, { state: 'PIPELINE_RUNNING' });
    }
    return touchApplicationUpdatedNow(app);
  });
}

function resolveApplicationPositionId(
  id: string,
  apps: Application[],
  targetRefs?: Record<string, string>,
): string | null {
  const row = apps.find((a) => a.id === id);
  const fromRow = row
    ? row.positionId || (row as Application & { jobPositionId?: string }).jobPositionId
    : null;
  if (fromRow) return fromRow;
  return targetRefs?.[id] ?? null;
}

/** Request refs for POST /bulk-processing-status (BE expects `positionId`, not `jobPositionId`). */
export function applicationsToBulkStatusRefs(
  apps: Application[],
): Array<{ applicationId: string; positionId: string }> {
  return apps
    .map((a) => {
      const positionId =
        a.positionId || (a as Application & { jobPositionId?: string }).jobPositionId;
      if (!positionId) return null;
      return { applicationId: a.id, positionId };
    })
    .filter((ref): ref is { applicationId: string; positionId: string } => ref != null);
}

function buildBulkStatusRefs(
  apps: Application[],
  ids: readonly string[],
  targetRefs?: Record<string, string>,
): Array<{ applicationId: string; positionId: string }> {
  return ids
    .map((id) => {
      const positionId = resolveApplicationPositionId(id, apps, targetRefs);
      if (!positionId) return null;
      return { applicationId: id, positionId };
    })
    .filter((ref): ref is { applicationId: string; positionId: string } => ref != null);
}

async function fetchApplicationBulkStatusUpdates(
  companyId: string,
  apps: Application[],
  ids: readonly string[],
  maxPerSync = HYDRATE_MAX_PER_SYNC,
  targetRefs?: Record<string, string>,
): Promise<Application[]> {
  if (ids.length === 0) return apps;

  const refs = buildBulkStatusRefs(apps, ids.slice(0, maxPerSync), targetRefs);
  if (refs.length === 0) return apps;

  let updatedApps = apps;
  for (let i = 0; i < refs.length; i += BULK_STATUS_API_MAX_PER_REQUEST) {
    const chunk = refs.slice(i, i + BULK_STATUS_API_MAX_PER_REQUEST);
    try {
      const items = await api.applications.bulkProcessingStatus(companyId, chunk);
      updatedApps = applyBulkStatusPatches(updatedApps, items);
    } catch (err) {
      if (!shouldFallbackBulkStatus(err)) {
        throw err;
      }
      const chunkIds = chunk.map((ref) => ref.applicationId);
      updatedApps = await fetchApplicationDetailUpdates(
        companyId,
        updatedApps,
        chunkIds,
        chunkIds.length,
        targetRefs,
      );
    }
  }
  return updatedApps;
}

async function fetchApplicationDetailUpdates(
  companyId: string,
  apps: Application[],
  ids: readonly string[],
  maxPerSync = HYDRATE_MAX_PER_SYNC,
  targetRefs?: Record<string, string>,
): Promise<Application[]> {
  if (ids.length === 0) return apps;

  const toFetch = ids.slice(0, maxPerSync);
  const updates = await mapWithConcurrency(toFetch, HYDRATE_CONCURRENCY, async (id) => {
    const positionId = resolveApplicationPositionId(id, apps, targetRefs);
    if (!positionId) return null;
    try {
      return await api.applications.get(companyId, positionId, id);
    } catch {
      return null;
    }
  });
  const resolved = updates.filter((a): a is Application => a != null);
  if (resolved.length === 0) return apps;
  return mergeApplicationsById(apps, resolved);
}

/** Hydrate selected rows from detail API before stop/abort decisions. */
export async function hydrateApplicationsFromDetail(
  companyId: string,
  apps: Application[],
  ids: readonly string[],
  targetRefs?: Record<string, string>,
): Promise<Application[]> {
  if (ids.length === 0) return apps;
  return fetchApplicationDetailUpdates(companyId, apps, ids, ids.length, targetRefs);
}

/**
 * Company list can lag behind application detail while pipelines finish.
 * Hydrate every bulk target that is not yet in a settled post-pipeline state.
 */
export async function hydrateUnsettledBulkTargetsFromDetail(
  companyId: string,
  apps: Application[],
  targetIds: readonly string[],
  options?: { maxPerSync?: number; targetRefs?: Record<string, string> },
): Promise<Application[]> {
  const pending = targetIds.filter((id) => {
    const row = apps.find((a) => a.id === id);
    return isBulkTargetPendingHydration(row);
  });
  return fetchApplicationBulkStatusUpdates(
    companyId,
    apps,
    pending,
    options?.maxPerSync ?? HYDRATE_MAX_PER_SYNC,
    options?.targetRefs,
  );
}

/**
 * Legacy path when session has processingIds but no targetIds.
 */
export async function hydrateInFlightBatchFromDetail(
  companyId: string,
  apps: Application[],
  targetIds: readonly string[],
  processingIds: Set<string>,
  targetRefs?: Record<string, string>,
): Promise<Application[]> {
  const pending = targetIds.filter((id) => {
    if (!processingIds.has(id)) return false;
    const row = apps.find((a) => a.id === id);
    return isBulkTargetPendingHydration(row);
  });
  return fetchApplicationBulkStatusUpdates(companyId, apps, pending, HYDRATE_MAX_PER_SYNC, targetRefs);
}
